import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import Sidebar from '../components/Sidebar';
import useExchangeRate from '../hooks/useExchangeRate';

const API = 'https://affinityconcept.tech/api';
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const COLORS = [
  '#1D9E75','#D85A30','#3B82F6','#F59E0B','#8B5CF6',
  '#EC4899','#06B6D4','#84CC16','#F97316','#6366F1','#14B8A6','#EF4444'
];

function daysUntil(dateStr) {
  const today = new Date();
  const due = new Date(dateStr);
  return Math.ceil((due - today) / (1000 * 60 * 60 * 24));
}

function urgencyColor(days) {
  if (days <= 7) return 'bg-red-50 text-red-500 border-red-200';
  if (days <= 14) return 'bg-orange-50 text-orange-500 border-orange-200';
  if (days <= 30) return 'bg-yellow-50 text-yellow-500 border-yellow-200';
  return 'bg-green-50 text-green-500 border-green-200';
}

function monthlyEquivalent(amount, frequency) {
  const map = { monthly: 1, quarterly: 1/3, yearly: 1/12, weekly: 4.33 };
  return amount * (map[frequency] || 1);
}

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-gray-100 rounded-xl shadow-lg p-3 text-sm">
        <p className="font-semibold text-gray-900">{payload[0].name}</p>
        <p className="text-gray-500">AED {payload[0].value?.toLocaleString(undefined, {maximumFractionDigits: 0})}</p>
        {payload[0].payload?.percent && <p className="text-gray-400">{payload[0].payload.percent}%</p>}
      </div>
    );
  }
  return null;
};

export default function Dashboard({ token, onLogout }) {
  const now = new Date();
  const [dashData, setDashData] = useState(null);
  const [user, setUser] = useState(null);
  const [bills, setBills] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [income, setIncome] = useState([]);
  const [propPayments, setPropPayments] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState({ year: now.getFullYear(), month: now.getMonth() + 1 });
  const [monthData, setMonthData] = useState(null);
  const [insights, setInsights] = useState([]);
  const [insightsLoading, setInsightsLoading] = useState(true);
  const [insightsError, setInsightsError] = useState(null);
  const [insightsAt, setInsightsAt] = useState(null);
  const { toAED } = useExchangeRate();

  const headers = { Authorization: `Bearer ${token}` };

  const loadInsights = (refresh = false) => {
    setInsightsLoading(true);
    setInsightsError(null);
    axios.get(`${API}/ai/insights${refresh ? '?refresh=true' : ''}`, { headers, timeout: 600000 })
      .then(r => {
        setInsights(r.data.insights || []);
        setInsightsAt(r.data.generated_at || null);
      })
      .catch(() => setInsightsError('Could not generate insights right now. Try refreshing.'))
      .finally(() => setInsightsLoading(false));
  };

  useEffect(() => {
    axios.get(`${API}/dashboard`, { headers }).then(r => setDashData(r.data)).catch(console.error);
    axios.get(`${API}/me`, { headers }).then(r => setUser(r.data)).catch(console.error);
    axios.get(`${API}/recurring-bills`, { headers }).then(r => setBills(r.data)).catch(console.error);
    axios.get(`${API}/expenses`, { headers }).then(r => setExpenses(r.data)).catch(console.error);
    axios.get(`${API}/income`, { headers }).then(r => setIncome(r.data)).catch(console.error);
    axios.get(`${API}/upcoming-property-payments`, { headers }).then(r => setPropPayments(r.data)).catch(console.error);
    loadInsights();
  }, [token]);

  useEffect(() => {
    axios.get(`${API}/monthly-summary/${selectedMonth.year}/${selectedMonth.month}`, { headers })
      .then(r => setMonthData(r.data))
      .catch(console.error);
  }, [selectedMonth]);

  const navigateMonth = (dir) => {
    setSelectedMonth(prev => {
      let m = prev.month + dir;
      let y = prev.year;
      if (m > 12) { m = 1; y++; }
      if (m < 1) { m = 12; y--; }
      return { year: y, month: m };
    });
  };

  const isCurrentMonth = selectedMonth.year === now.getFullYear() && selectedMonth.month === now.getMonth() + 1;
  const isFutureMonth = selectedMonth.year > now.getFullYear() || (selectedMonth.year === now.getFullYear() && selectedMonth.month > now.getMonth() + 1);
  const selectedMonthName = new Date(selectedMonth.year, selectedMonth.month - 1, 1).toLocaleDateString('en-AE', { month: 'long', year: 'numeric' });

  // Monthly calculations
  const monthlyFixed = bills.reduce((sum, b) => sum + toAED(monthlyEquivalent(b.amount, b.frequency), b.currency), 0);

  // Actual fixed costs due in a specific calendar month, driven by each bill's
  // real next_due_date + frequency:
  //   - monthly/weekly bills → always count (smoothed to a monthly equivalent)
  //   - quarterly bills      → count when the month is on the 3-month cycle from due
  //   - yearly bills         → count only in the month matching the due date
  // Returns the total plus the list of periodic (non-monthly) bills that land
  // that month, so the UI can note "includes Rent" / "includes School fees".
  const fixedForMonth = (year, monthIdx) => {
    let total = 0;
    const periodic = [];
    bills.forEach(b => {
      const dueMonth = new Date(b.next_due_date).getMonth();
      const amt = toAED(b.amount, b.currency);
      if (b.frequency === 'quarterly') {
        if ((((monthIdx - dueMonth) % 3) + 3) % 3 === 0) { total += amt; periodic.push(b); }
      } else if (b.frequency === 'yearly') {
        if (monthIdx === dueMonth) { total += amt; periodic.push(b); }
      } else {
        total += toAED(monthlyEquivalent(b.amount, b.frequency), b.currency);
      }
    });
    return { total, periodic };
  };

  const thisMonthExpenses = expenses.filter(e => {
    const d = new Date(e.date);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });
  const thisMonthSpent = thisMonthExpenses.reduce((sum, e) => sum + toAED(e.amount, e.currency), 0);

// Average monthly variable spending (last 3 months)
const avgMonthlyVariable = (() => {
    const filteredExpenses = expenses.filter(e => !e.exclude_from_forecast);
    const currentMonthFiltered = filteredExpenses
      .filter(e => {
        const d = new Date(e.date);
        return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
      })
      .reduce((sum, e) => sum + toAED(e.amount, e.currency), 0);

    const monthTotals = [1, 2, 3].map(offset => {
      const d = new Date(now.getFullYear(), now.getMonth() - offset, 1);
      return filteredExpenses
        .filter(e => {
          const ed = new Date(e.date);
          return ed.getMonth() === d.getMonth() && ed.getFullYear() === d.getFullYear();
        })
        .reduce((sum, e) => sum + toAED(e.amount, e.currency), 0);
    }).filter(m => m > 0);

    if (monthTotals.length === 0) return currentMonthFiltered || 8000;
    return monthTotals.reduce((s, m) => s + m, 0) / monthTotals.length;
  })();

  const monthlyRecurring = income
    .filter(i => i.is_recurring && i.recurring_frequency === 'monthly')
    .reduce((sum, i) => sum + toAED(i.amount, i.currency), 0);

  const thisMonthOneTime = income
    .filter(i => {
      if (i.is_recurring) return false;
      const d = new Date(i.date);
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    })
    .reduce((sum, i) => sum + toAED(i.amount, i.currency), 0);

  const monthlyIncome = monthlyRecurring + thisMonthOneTime;
  const cashFlow = monthlyIncome - monthlyFixed - thisMonthSpent;
  const cashFlowFiltered = monthlyIncome - monthlyFixed - avgMonthlyVariable;

  // Selected month data
  const selectedExpenses = monthData?.expenses || [];
  const selectedSpent = isFutureMonth ? avgMonthlyVariable : isCurrentMonth ? thisMonthSpent : (monthData?.total_expenses || 0);
const selectedOneTime = income
    .filter(i => {
      if (i.is_recurring) return false;
      const d = new Date(i.date);
      return d.getMonth() === selectedMonth.month - 1 && d.getFullYear() === selectedMonth.year;
    })
    .reduce((sum, i) => sum + toAED(i.amount, i.currency), 0);

const selectedIncome = monthlyRecurring + selectedOneTime;
  const displayIncome = selectedIncome;

  // Real fixed costs for the selected month (includes periodic spikes).
  const selectedFixedInfo = fixedForMonth(selectedMonth.year, selectedMonth.month - 1);
  const selectedFixed = selectedFixedInfo.total;
  const selectedFixedNote = (() => {
    const p = selectedFixedInfo.periodic;
    if (!p.length) return `${bills.length} recurring bills`;
    const hasRent = p.some(b => (b.name || '').toLowerCase().includes('rent'));
    const hasSchool = p.some(b => (b.name || '').toLowerCase().includes('school'));
    const tags = [];
    if (hasRent) tags.push('Rent');
    if (hasSchool) tags.push('School fees');
    const others = p.filter(b => {
      const n = (b.name || '').toLowerCase();
      return !n.includes('rent') && !n.includes('school');
    });
    if (others.length) tags.push(`${others.length} annual bill${others.length > 1 ? 's' : ''}`);
    return tags.length ? `includes ${tags.join(' + ')}` : `${bills.length} recurring bills`;
  })();

  const selectedFlow = displayIncome - selectedFixed - selectedSpent;

  // Upcoming bills + property payment-plan installments (next 30 days)
  const upcomingBills = bills
    .filter(b => {
      const days = daysUntil(b.next_due_date);
      return days >= 0 && days <= 30;
    })
    .map(b => ({ key: `bill-${b.id}`, name: b.name, due_date: b.next_due_date, amount: b.amount, currency: b.currency || 'AED', kind: 'bill' }));
  const upcomingInstallments = propPayments
    .filter(p => {
      const days = daysUntil(p.due_date);
      return days >= 0 && days <= 30;
    })
    .map(p => ({ key: `inst-${p.id}`, name: `🏗️ ${p.property_name} — ${p.label}`, due_date: p.due_date, amount: p.amount, currency: p.currency || 'AED', kind: 'installment' }));
  const upcoming = [...upcomingBills, ...upcomingInstallments]
    .sort((a, b) => daysUntil(a.due_date) - daysUntil(b.due_date))
    .slice(0, 6);

  // Pie chart
  const categoryTotals = (isCurrentMonth ? thisMonthExpenses : isFutureMonth ? expenses.filter(e => !e.exclude_from_forecast) : selectedExpenses).reduce((acc, e) => {
    acc[e.category] = (acc[e.category] || 0) + e.amount;
    return acc;
  }, {});

  const fixedByCategory = bills.reduce((acc, b) => {
    acc[b.category] = (acc[b.category] || 0) + toAED(monthlyEquivalent(b.amount, b.frequency), b.currency);
    return acc;
  }, {});

  const allCategories = { ...fixedByCategory };
  Object.entries(categoryTotals).forEach(([k, v]) => {
    allCategories[k] = (allCategories[k] || 0) + v;
  });

  const totalAll = Object.values(allCategories).reduce((s, v) => s + v, 0);
  const pieData = Object.entries(allCategories)
    .sort((a, b) => b[1] - a[1])
    .map(([name, value]) => ({
      name,
      value: Math.round(value),
      percent: ((value / totalAll) * 100).toFixed(1)
    }));

  // 12-month forecast
  const forecastData = Array.from({ length: 12 }, (_, i) => {
    const date = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const monthIndex = date.getMonth();
    const yearIndex = date.getFullYear();
    const label = MONTHS[monthIndex];

    let monthIncome = monthlyRecurring || 30000;
    const oneTimeThisMonth = income
      .filter(inc => {
        if (inc.is_recurring) return false;
        const d = new Date(inc.date);
        return d.getMonth() === monthIndex && d.getFullYear() === yearIndex;
      })
      .reduce((sum, inc) => sum + toAED(inc.amount, inc.currency), 0);
    monthIncome += oneTimeThisMonth;

    // Real fixed costs for this month, including periodic spikes (rent, school, etc.)
    const fixed = fixedForMonth(yearIndex, monthIndex).total;

    const variable = i === 0 ? thisMonthSpent : avgMonthlyVariable;

    return {
      month: label,
      income: Math.round(monthIncome),
      fixed: Math.round(fixed),
      variable: Math.round(variable),
      expenses: Math.round(fixed + variable),
    };
  });

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <Sidebar onLogout={onLogout} user={user} />
      <main className="flex-1 overflow-y-auto p-4 md:p-8 pt-16 md:pt-8">

        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{greeting()}, {user?.name} 👋</h1>
            <p className="text-gray-500 text-sm">
              {new Date().toLocaleDateString('en-AE', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
        </div>

        {/* Month Navigator */}
        <div className="flex items-center justify-between bg-white rounded-2xl px-5 py-3 shadow-sm border border-gray-100 mb-6">
          <button onClick={() => navigateMonth(-1)}
            className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-xl transition text-gray-500 font-bold text-xl">
            ‹
          </button>
          <div className="text-center">
            <p className="text-sm font-semibold text-gray-900">{selectedMonthName}</p>
            <p className="text-xs text-gray-400">
              {isCurrentMonth ? '📍 Current month' : isFutureMonth ? '🔮 Forecast' : '📊 Historical data'}
            </p>
          </div>
          <button onClick={() => navigateMonth(1)}
            className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-xl transition text-gray-500 font-bold text-xl">
            ›
          </button>
        </div>

        {/* Metric cards */}
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <p className="text-xs text-gray-500 mb-1">{isFutureMonth ? 'Expected Income' : 'Income'}</p>
            <p className="text-xl font-bold text-green-500">
              AED {Math.round(selectedIncome).toLocaleString()}
            </p>
            <p className="text-xs text-gray-400 mt-1">
          {`Recurring: AED ${Math.round(monthlyRecurring).toLocaleString()}${selectedOneTime > 0 ? ` + One-time: AED ${Math.round(selectedOneTime).toLocaleString()}` : ''}`}            </p>
          </div>
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <p className="text-xs text-gray-500 mb-1">Fixed Costs</p>
            <p className="text-xl font-bold text-red-500">AED {Math.round(selectedFixed).toLocaleString()}</p>
            <p className="text-xs text-gray-400 mt-1">{selectedFixedNote}</p>
          </div>
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <p className="text-xs text-gray-500 mb-1">{isFutureMonth ? 'Est. Variable' : 'Variable Expenses'}</p>
            <p className="text-xl font-bold text-orange-500">
              AED {Math.round(selectedSpent).toLocaleString()}
            </p>
            <p className="text-xs text-gray-400 mt-1">
              {isFutureMonth ? 'Based on average' : `${selectedExpenses.length} transactions`}
            </p>
          </div>
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <p className="text-xs text-gray-500 mb-1">Net Cash Flow</p>
            <p className={`text-xl font-bold ${selectedFlow >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              {selectedFlow >= 0 ? '+' : ''}AED {Math.round(selectedFlow).toLocaleString()}
            </p>
            <p className="text-xs text-gray-400 mt-1">Income - all costs</p>
          </div>
        </div>

        {/* AI Insights */}
        <div className="bg-blue-50 rounded-2xl p-5 shadow-sm border border-blue-100 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-blue-900">💡 AI Spending Insights</h2>
            <button onClick={() => loadInsights(true)} disabled={insightsLoading}
              className="text-xs font-medium text-blue-600 hover:text-blue-800 disabled:opacity-40 transition">
              {insightsLoading ? '…' : '🔄 Refresh'}
            </button>
          </div>
          {insightsLoading ? (
            <div className="flex items-center gap-2 text-sm text-blue-500 py-2">
              <span className="w-3 h-3 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin"></span>
              Analyzing your spending… this can take up to 30 seconds.
            </div>
          ) : insightsError ? (
            <p className="text-sm text-blue-400">{insightsError}</p>
          ) : insights.length === 0 ? (
            <p className="text-sm text-blue-400">No insights yet. Hit refresh to generate.</p>
          ) : (
            <>
              <ul className="space-y-2">
                {insights.map((tip, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-blue-900">
                    <span className="text-blue-400 mt-0.5">•</span>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
              {insightsAt && (
                <p className="text-xs text-blue-400 mt-3">
                  Updated {new Date(insightsAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                </p>
              )}
            </>
          )}
        </div>

        {/* Smoothed average burn rate */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-gray-900">📊 Average Monthly Burn Rate</h2>
            <span className="text-xs text-gray-400">Annual costs ÷ 12 — smoothed across the year</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-500 mb-1">Average Monthly Fixed Cost</p>
              <p className="text-2xl font-bold text-red-500">AED {Math.round(monthlyFixed).toLocaleString()}</p>
              <p className="text-xs text-gray-400 mt-1">All {bills.length} recurring bills, smoothed</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-500 mb-1">Average Monthly Total (incl. variable)</p>
              <p className="text-2xl font-bold text-gray-900">AED {Math.round(monthlyFixed + avgMonthlyVariable).toLocaleString()}</p>
              <p className="text-xs text-gray-400 mt-1">Fixed + avg variable (AED {Math.round(avgMonthlyVariable).toLocaleString()})</p>
            </div>
          </div>
        </div>

        {/* Upcoming payments + Pie chart */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <h2 className="text-sm font-semibold text-gray-900 mb-4">🔔 Upcoming payments</h2>
            {upcoming.length === 0 && <p className="text-sm text-gray-400">No payments due in next 30 days</p>}
            <div className="space-y-3">
              {upcoming.map(item => {
                const days = daysUntil(item.due_date);
                return (
                  <div key={item.key} className={`flex items-center justify-between p-3 rounded-xl border ${urgencyColor(days)}`}>
                    <div>
                      <p className="text-sm font-medium">{item.name}</p>
                      <p className="text-xs opacity-70">
                        {new Date(item.due_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                        {' · '}
                        {days === 0 ? 'Today!' : days === 1 ? 'Tomorrow' : `${days} days`}
                      </p>
                    </div>
                    <p className="text-sm font-bold">{item.currency} {item.amount.toLocaleString()}</p>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <h2 className="text-sm font-semibold text-gray-900 mb-1">💰 Average Monthly Breakdown</h2>
            <p className="text-xs text-gray-400 mb-3">Average distribution incl. rent & school smoothed</p>
            {pieData.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8">No data yet</p>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={2} dataKey="value">
                    {pieData.map((entry, index) => (
                      <Cell key={index} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            )}
            <div className="grid grid-cols-2 gap-1 mt-2">
              {pieData.slice(0, 8).map((item, i) => (
                <div key={i} className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }}></span>
                  <span className="text-xs text-gray-500 truncate">{item.name}</span>
                  <span className="text-xs text-gray-400 ml-auto">{item.percent}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 12-month forecast */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 mb-6">
          <h2 className="text-sm font-semibold text-gray-900 mb-1">📈 12-Month Forecast</h2>
          <p className="text-xs text-gray-400 mb-4">Projected income vs total expenses</p>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={forecastData}>
              <defs>
                <linearGradient id="incomeGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#1D9E75" stopOpacity={0.15}/>
                  <stop offset="95%" stopColor="#1D9E75" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="expensesGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#D85A30" stopOpacity={0.15}/>
                  <stop offset="95%" stopColor="#D85A30" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={v => `AED ${v.toLocaleString()}`} />
              <Area type="monotone" dataKey="income" stroke="#1D9E75" fill="url(#incomeGrad)" strokeWidth={2} name="Income" />
              <Area type="monotone" dataKey="expenses" stroke="#D85A30" fill="url(#expensesGrad)" strokeWidth={2} name="Expenses" />
            </AreaChart>
          </ResponsiveContainer>
          <div className="flex gap-6 mt-3 text-xs">
            <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-green-500 inline-block"></span>Income</span>
            <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-red-500 inline-block"></span>Total Expenses</span>
          </div>
        </div>

        {/* Cash flow next 6 months */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 mb-6">
          <h2 className="text-sm font-semibold text-gray-900 mb-1">💵 Cash Flow — Next 6 Months</h2>
          <p className="text-xs text-gray-400 mb-4">Projected net cash flow after all fixed costs</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Month</th>
                  <th className="text-right p-3 text-xs font-medium text-gray-500 uppercase">Income</th>
                  <th className="text-right p-3 text-xs font-medium text-gray-500 uppercase">Fixed</th>
                  <th className="text-right p-3 text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Variable</th>
                  <th className="text-right p-3 text-xs font-medium text-gray-500 uppercase">Net Flow</th>
                </tr>
              </thead>
              <tbody>
                {forecastData.slice(0, 6).map((row, i) => {
                  const net = row.income - row.expenses;
                  return (
                    <tr key={i} className={`border-b border-gray-50 ${i === 0 ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                      <td className="p-3 font-medium text-gray-900">
                        {row.month}
                        {i === 0 && <span className="ml-2 text-xs text-blue-500 font-normal">current</span>}
                      </td>
                      <td className="p-3 text-right text-green-500 font-medium">AED {row.income.toLocaleString()}</td>
                      <td className="p-3 text-right text-red-500">AED {row.fixed.toLocaleString()}</td>
                      <td className="p-3 text-right text-orange-500 hidden md:table-cell">AED {row.variable.toLocaleString()}</td>
                      <td className="p-3 text-right font-bold">
                        <span className={net >= 0 ? 'text-green-500' : 'text-red-500'}>
                          {net >= 0 ? '+' : ''}AED {net.toLocaleString()}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-200 bg-gray-50">
                  <td className="p-3 font-semibold text-gray-700">6-Month Total</td>
                  <td className="p-3 text-right font-semibold text-green-500">
                    AED {forecastData.slice(0, 6).reduce((s, r) => s + r.income, 0).toLocaleString()}
                  </td>
                  <td className="p-3 text-right font-semibold text-red-500">
                    AED {forecastData.slice(0, 6).reduce((s, r) => s + r.fixed, 0).toLocaleString()}
                  </td>
                  <td className="p-3 text-right font-semibold text-orange-500 hidden md:table-cell">
                    AED {forecastData.slice(0, 6).reduce((s, r) => s + r.variable, 0).toLocaleString()}
                  </td>
                  <td className="p-3 text-right font-bold">
                    {(() => {
                      const total6 = forecastData.slice(0, 6).reduce((s, r) => s + r.income - r.expenses, 0);
                      return <span className={total6 >= 0 ? 'text-green-500' : 'text-red-500'}>{total6 >= 0 ? '+' : ''}AED {total6.toLocaleString()}</span>;
                    })()}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* This month variable expenses */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <h2 className="text-sm font-semibold text-gray-900 mb-4">
            💸 {isFutureMonth ? 'Estimated' : selectedMonthName} variable expenses
          </h2>
          {(isCurrentMonth ? thisMonthExpenses : isFutureMonth ? expenses.filter(e => !e.exclude_from_forecast) : selectedExpenses).length === 0 ? (
            <p className="text-sm text-gray-400">No expenses logged for this month</p>
          ) : (
            <div className="space-y-2">
              {Object.entries(isFutureMonth ? categoryTotals : (monthData?.by_category || {}))
                .sort((a, b) => b[1] - a[1])
                .map(([cat, amount], i) => {
                  const total = isCurrentMonth ? thisMonthSpent : isFutureMonth ? avgMonthlyVariable : (monthData?.total_expenses || 1);
                  const pct = ((amount / total) * 100).toFixed(0);
                  return (
                    <div key={cat}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-gray-600">{cat}</span>
                        <span className="font-medium text-gray-900">
                          AED {amount.toLocaleString()}
                          <span className="text-gray-400 ml-1">({pct}%)</span>
                        </span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all"
                          style={{ width: `${pct}%`, backgroundColor: COLORS[i % COLORS.length] }}>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>

      </main>
    </div>
  );
}
